import React from "react";
import PropTypes from "prop-types";

// local component imports
import GridCell from "./GridCell";

/**
 * BentoGrid places cells in a CSS grid using a packing algorithm for arbitrary col/row spans.
 * @param {object} props - Component props.
 * @param {Array} props.rows - Row config array from gridConfig.
 * @param {object} props.gridConfig - Grid configuration (gaps, columns, etc.).
 * @param {Map} props.contentMap - Map of cell keys to content data.
 * @param {object} props.videoRefs - Ref map to video DOM elements.
 * @param {Function} props.onToggleVideoPlay - Callback to toggle video play state.
 * @param {object} props.gridTextStyle - Text style settings for grid cells.
 * @param {boolean} props.isAboveFold - Whether this grid is expected above the fold.
 * @param {boolean} props.isLcpCandidate - Whether this grid may contain the LCP image.
 * @returns {React.ReactElement} The bento grid layout.
 */
const BentoGrid = ({
  rows,
  gridConfig,
  contentMap,
  videoRefs,
  onToggleVideoPlay,
  gridTextStyle,
  gridBorder,
  isMobile,
  isAboveFold,
  isLcpCandidate,
}) => {
  const borderThickness = gridBorder?.borderThickness ?? 0;
  const borderColor = gridBorder?.borderColor || "transparent";
  const borderStyle =
    borderThickness > 0 ? `${borderThickness}px solid ${borderColor}` : isMobile ? undefined : "2px solid transparent";

  const maxColumns = Math.max(...rows.map((r) => r.columns || 1), 1);
  const occupied = new Set();
  const positioned = [];

  /**
   * Returns a stable key for a cell object.
   * @param {object} cell - The cell object.
   * @param {string} fallback - Fallback key string.
   * @returns {string} The cell key.
   */
  const getCellKey = (cell, fallback) => cell.cellId || cell.id || fallback;

  /**
   * Merges cell config with content map data.
   * @param {object} cell - The raw cell config.
   * @param {string} fallbackKey - Fallback key if cell has no id.
   * @returns {object} Merged cell object.
   */
  const mergeCell = (cell, fallbackKey) => {
    const key = getCellKey(cell, fallbackKey);
    const fromContent = contentMap.get(key);
    return fromContent ? { ...cell, ...fromContent } : cell;
  };

  /**
   * Checks if a cell can be placed at the given grid position.
   * @param {number} r - Row index.
   * @param {number} c - Column index.
   * @param {number} rowSpan - Number of rows the cell spans.
   * @param {number} colSpan - Number of columns the cell spans.
   * @returns {boolean} True if the position is available.
   */
  const canPlace = (r, c, rowSpan, colSpan) => {
    if (c + colSpan - 1 > maxColumns) return false;
    for (let rr = r; rr < r + rowSpan; rr += 1) {
      for (let cc = c; cc < c + colSpan; cc += 1) {
        if (occupied.has(`${rr}-${cc}`)) return false;
      }
    }
    return true;
  };

  /**
   * Marks grid positions as occupied.
   * @param {number} r - Row index.
   * @param {number} c - Column index.
   * @param {number} rowSpan - Number of rows to mark.
   * @param {number} colSpan - Number of columns to mark.
   */
  const markPlaced = (r, c, rowSpan, colSpan) => {
    for (let rr = r; rr < r + rowSpan; rr += 1) {
      for (let cc = c; cc < c + colSpan; cc += 1) {
        occupied.add(`${rr}-${cc}`);
      }
    }
  };

  let currentRow = 1;
  rows.forEach((row, rowIndex) => {
    row.cells.forEach((rawCell, cellIndex) => {
      const merged = mergeCell(rawCell, `${row.id}-${cellIndex}`);
      const colSpan = Math.max(1, merged.colSpan || 1);
      const rowSpan = Math.max(1, merged.rowSpan || 1);
      let r = currentRow;
      let c = 1;
      let placed = false;
      while (!placed) {
        c = 1;
        while (c <= maxColumns) {
          if (canPlace(r, c, rowSpan, colSpan)) {
            markPlaced(r, c, rowSpan, colSpan);
            positioned.push({
              key: getCellKey(merged, `${row.id}-${cellIndex}`),
              cell: merged,
              rowStart: r,
              colStart: c,
              rowSpan,
              colSpan,
            });
            placed = true;
            break;
          }
          c += 1;
        }
        if (!placed) r += 1;
      }
    });
    currentRow = rowIndex + 2;
  });

  const totalRows = positioned.reduce((max, p) => Math.max(max, p.rowStart + p.rowSpan - 1), 1);

  return (
    <div
      className="grid w-full"
      style={{
        gridTemplateColumns: `repeat(${maxColumns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${totalRows}, auto)`,
        rowGap: gridConfig.rowGap ?? 0,
        columnGap: gridConfig.columnGap ?? 0,
      }}
    >
      {positioned.map((p, index) => {
        const borderRadius = p.cell.borderRadius ?? gridBorder?.borderRadius ?? gridConfig.borderRadius ?? 0;
        const ratio = p.cell.aspectRatio;

        return (
          <div
            key={p.key}
            style={{
              gridColumn: `${p.colStart} / span ${p.colSpan}`,
              gridRow: `${p.rowStart} / span ${p.rowSpan}`,
            }}
          >
            <GridCell
              cell={p.cell}
              index={index}
              onVideoToggle={onToggleVideoPlay}
              videoRefs={videoRefs}
              gridTextStyle={gridTextStyle}
              columnCount={maxColumns}
              isMobile={isMobile}
              aspectRatio={ratio}
              cellBorderRadius={p.cell.borderRadius ?? borderRadius}
              cellBorderStyle={borderStyle}
              cellBorderThickness={borderThickness}
              isEager={isAboveFold}
              isPriority={isLcpCandidate}
            />
          </div>
        );
      })}
    </div>
  );
};

BentoGrid.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  gridConfig: PropTypes.object.isRequired,
  contentMap: PropTypes.instanceOf(Map).isRequired,
  videoRefs: PropTypes.object.isRequired,
  onToggleVideoPlay: PropTypes.func.isRequired,
  gridTextStyle: PropTypes.object,
  gridBorder: PropTypes.shape({
    borderRadius: PropTypes.number,
    borderThickness: PropTypes.number,
    borderColor: PropTypes.string,
  }),
  isMobile: PropTypes.bool.isRequired,
  isAboveFold: PropTypes.bool,
  isLcpCandidate: PropTypes.bool,
};

export default BentoGrid;
